import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useSession } from '../api/session';
import { useApi } from '../api/useApi';
import type { NavItem } from './navigation';

/** Fired by the approval inbox after a decision, so the count does not wait for the next navigation. */
export const APPROVALS_CHANGED_EVENT = 'approvals-changed';

export type NavBadges = Partial<Record<NonNullable<NavItem['badge']>, number>>;

/**
 * Live counts beside navigation entries — today only "awaiting my decision".
 *
 * Refetched on navigation and when the inbox announces a decision, the same pattern as the
 * notification bell: no polling, one small request (`limit=1`, the scoped total) per screen change,
 * and only for someone who may decide and read requests. Anyone else sends nothing.
 */
export function useNavBadges(): NavBadges {
  const { can, canAny } = useSession();
  const location = useLocation();
  const eligible =
    can('approval.request.view') && canAny(['approval.request.approve', 'approval.request.reject']);
  const awaiting = useApi<{ total: number }>(
    eligible ? '/api/v1/approvals/requests?awaitingMe=true&limit=1' : undefined,
  );
  const { reload } = awaiting;

  useEffect(() => {
    if (eligible) reload();
  }, [eligible, location.pathname, reload]);

  useEffect(() => {
    window.addEventListener(APPROVALS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(APPROVALS_CHANGED_EVENT, reload);
  }, [reload]);

  return awaiting.state.kind === 'ready' && awaiting.state.data.total > 0
    ? { approvalsAwaiting: awaiting.state.data.total }
    : {};
}
